import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Box, Container, Typography, CircularProgress, Button } from '@mui/material'
import { publicNorthStarApi, PublicNorthStarResponse } from '../services/api'
import { ApiError } from '../services/api'
import NorthStarDocument, { useNorthStarPageStyles } from '../components/north-star/NorthStarDocument'

function NotFound() {
  return (
    <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
      <Typography
        sx={{
          fontFamily: 'var(--ns-display-font)',
          fontSize: 32,
          fontWeight: 500,
        }}
      >
        This page isn't available.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        The link you opened may have been deactivated or rotated by the MissFit Mindset team.
      </Typography>
      <Button
        component="a"
        href="https://missfitmindset.ai"
        variant="outlined"
        sx={{ mt: 4 }}
      >
        Visit missfitmindset.ai
      </Button>
    </Container>
  )
}

export default function NorthStarPublic() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const preview = searchParams.get('preview') === '1'
  const [data, setData] = useState<PublicNorthStarResponse | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)

  useNorthStarPageStyles()

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setNotFound(false)
        const resp = await publicNorthStarApi.getBySlug(slug, preview)
        if (!cancelled) setData(resp)
      } catch (err) {
        const apiErr = err as ApiError
        if (apiErr?.status === 404) {
          if (!cancelled) setNotFound(true)
        } else {
          console.error(err)
          if (!cancelled) setNotFound(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    // Fire-and-forget view log
    if (!preview) {
      publicNorthStarApi.logView(slug)
    }
    return () => {
      cancelled = true
    }
  }, [slug, preview])

  useEffect(() => {
    if (data?.client?.name) {
      document.title = `${data.client.name} — North Star`
    }
  }, [data])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (notFound || !data) {
    return <NotFound />
  }

  return (
    <NorthStarDocument
      sections={data.sections || []}
      templates={data.templates}
      clientName={data.client?.name || ''}
      currentRole={data.client?.currentRole || ''}
      publishedAt={data.publishedAt}
      disclaimer={data.meta?.compDisclaimer}
      banner={
        preview ? (
          <Box
            className="ns-no-print"
            sx={{ background: '#fff3cd', color: '#664d03', py: 1, textAlign: 'center', fontSize: 13 }}
          >
            PREVIEW · Not visible to recruiters until you Publish
          </Box>
        ) : undefined
      }
    />
  )
}
